import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { resolveAssetUrl } from '@/lib/tenantBranding';

export type DoctorTeamBrandingForm = {
  teamPortalPrimaryColor: string;
  teamPortalSecondaryColor: string;
  teamPortalAccentColor: string;
  teamPortalSidebarBgColor: string;
  teamPortalCustomUiColors: boolean;
  teamPortalLogoUrl?: string;
};

type Props = {
  value: DoctorTeamBrandingForm;
  onChange: (next: DoctorTeamBrandingForm) => void;
  logoFile: File | null;
  onLogoFileChange: (file: File | null) => void;
  existingLogoUrl?: string | null;
};

const colorFields = [
  { key: 'teamPortalPrimaryColor' as const, label: 'Primary color' },
  { key: 'teamPortalSecondaryColor' as const, label: 'Secondary color' },
  { key: 'teamPortalAccentColor' as const, label: 'Accent color' },
  { key: 'teamPortalSidebarBgColor' as const, label: 'Sidebar background' },
];

export function DoctorTeamBrandingFields({
  value,
  onChange,
  logoFile,
  onLogoFileChange,
  existingLogoUrl,
}: Props) {
  const previewUrl = logoFile ? URL.createObjectURL(logoFile) : resolveAssetUrl(existingLogoUrl);

  return (
    <div className="space-y-3 rounded-md border p-3 bg-muted/30">
      <div>
        <p className="text-sm font-medium">Team portal branding</p>
        <p className="text-xs text-muted-foreground mt-1">
          Logo appears beside the hospital logo in the team lead and team doctor logins. Colors apply when
          custom UI is enabled.
        </p>
      </div>
      <div className="space-y-2">
        <Label htmlFor="team-portal-logo">Doctor logo</Label>
        <Input
          id="team-portal-logo"
          type="file"
          accept="image/*"
          onChange={(e) => onLogoFileChange(e.target.files?.[0] ?? null)}
        />
        {previewUrl && (
          <img src={previewUrl} alt="Team logo preview" className="h-12 max-w-[160px] object-contain rounded border bg-white p-1" />
        )}
      </div>
      <div className="flex items-center gap-2">
        <Checkbox
          id="team-custom-colors"
          checked={value.teamPortalCustomUiColors}
          onCheckedChange={(c) => onChange({ ...value, teamPortalCustomUiColors: c === true })}
        />
        <Label htmlFor="team-custom-colors">Use custom UI colors for team portal</Label>
      </div>
      {value.teamPortalCustomUiColors && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {colorFields.map((f) => (
            <div key={f.key} className="space-y-1">
              <Label className="text-xs">{f.label}</Label>
              <div className="flex gap-2 items-center">
                <Input
                  type="color"
                  className="h-9 w-12 p-1"
                  value={value[f.key] || '#6F553D'}
                  onChange={(e) => onChange({ ...value, [f.key]: e.target.value })}
                />
                <Input
                  value={value[f.key]}
                  onChange={(e) => onChange({ ...value, [f.key]: e.target.value })}
                  placeholder="#6F553D"
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
